import { useCallback, useEffect, useState } from 'react';
import { Colonia } from '../../types/colonia';
import { calcularIdade, turmaPorIdade, TURMAS } from '../formularioParts/utils';

const adminApiBaseUrl =
  process.env.REACT_APP_PAYMENT_API_URL ||
  'https://southamerica-east1-coloniadeferiasss.cloudfunctions.net/paymentApi';
const fallbackInscricaoDate = new Date(2026, 0, 1);

type DateLike =
  | Date
  | string
  | number
  | {
      seconds?: number;
      _seconds?: number;
      nanoseconds?: number;
      toDate?: () => Date;
    }
  | null
  | undefined;

const normalizeString = (value: unknown, fallback = '') =>
  typeof value === 'string' && value.trim() ? value.trim() : fallback;

const normalizeBoolean = (value: unknown, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return ['sim', 'true', '1', 'yes', 'pago'].includes(value.toLowerCase());
  return fallback;
};

const normalizeNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsedValue = Number(value.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  }
  return fallback;
};

export const parseInscricaoDate = (rawDate: DateLike) => {
  if (!rawDate) return null;

  if (rawDate instanceof Date) {
    return Number.isNaN(rawDate.getTime()) ? null : rawDate;
  }

  if (typeof rawDate === 'number') {
    const timestamp = rawDate < 10000000000 ? rawDate * 1000 : rawDate;
    const parsedDate = new Date(timestamp);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  if (typeof rawDate === 'object') {
    if (typeof rawDate.toDate === 'function') {
      const parsedDate = rawDate.toDate();
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    const seconds = rawDate.seconds ?? rawDate._seconds;
    if (typeof seconds === 'number') {
      const parsedDate = new Date(seconds * 1000);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
  }

  const trimmedDate = rawDate.trim();
  if (!trimmedDate) return null;

  const brazilianDateMatch = trimmedDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[,\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (brazilianDateMatch) {
    const [, day, month, year, hour = '0', minute = '0', second = '0'] = brazilianDateMatch;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const isoDateMatch = trimmedDate.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[,\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (isoDateMatch) {
    const [, year, month, day, hour = '0', minute = '0', second = '0'] = isoDateMatch;
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const parsedDate = new Date(trimmedDate);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getInscricaoDate = (inscricao: Colonia) => {
  const rawInscricao = inscricao as Colonia & Record<string, DateLike>;
  const candidates: DateLike[] = [
    rawInscricao.createdAt,
    rawInscricao.timestamp,
    rawInscricao.confirmedAt,
    rawInscricao.paymentConfirmedAt,
    rawInscricao.updatedAt,
    rawInscricao.dataConsentimento,
    rawInscricao.dataInscricao,
    rawInscricao.created_at,
    rawInscricao.dateCreated,
    rawInscricao.paymentDate
  ];

  for (const candidate of candidates) {
    const parsedDate = parseInscricaoDate(candidate);
    if (parsedDate) return parsedDate;
  }

  return fallbackInscricaoDate;
};

export const getAdminPeriodFromDate = (date: Date) => {
  const year = date.getFullYear();

  if (year === 2026 && date >= new Date(2026, 5, 1)) {
    return '2026.2';
  }

  return String(year);
};

export const getCurrentAdminPeriod = () => getAdminPeriodFromDate(new Date());

export const getInscricaoYear = (inscricao: Colonia) => getAdminPeriodFromDate(getInscricaoDate(inscricao));

export const getAvailableAdminPeriods = (inscricoes: Colonia[]) =>
  Array.from(new Set([...inscricoes.map(getInscricaoYear), '2026.2'])).sort((a, b) => Number(b) - Number(a));

export const normalizeInscricao = (raw: Record<string, unknown>, index: number): Colonia | null => {
  const nomeCompleto =
    normalizeString(raw.nomeCompleto) ||
    normalizeString(raw.nomeCrianca) ||
    normalizeString(raw.nome) ||
    normalizeString(raw.childName);

  if (!nomeCompleto) return null;

  const nomeMae =
    normalizeString(raw.nomeMae) ||
    normalizeString(raw.responsavel) ||
    normalizeString(raw.nomeResponsavel) ||
    normalizeString(raw.guardianName, 'Responsavel nao informado');
  const telefoneMae =
    normalizeString(raw.telefoneMae) ||
    normalizeString(raw.whatsapp) ||
    normalizeString(raw.telefoneResponsavel) ||
    normalizeString(raw.telefoneResidencial) ||
    normalizeString(raw.phone, '-');
  const valorTotal = normalizeNumber(raw.valorTotal ?? raw.total ?? raw.value ?? raw.valor ?? raw.valorInscricao, 0);
  const createdAt = getInscricaoDate(raw as unknown as Colonia);

  // Idade e turma gravadas nem sempre são confiáveis (há registros com idade 0 e turma vazia).
  // Quando faltam, derivamos da data de nascimento — que aceita dd/mm/aaaa e aaaa-mm-dd.
  const dataNascimento = normalizeString(raw.dataNascimento, '2018-01-01');
  const idadeGravada = normalizeNumber(raw.idade, 0);
  const idade = idadeGravada > 0 ? idadeGravada : calcularIdade(dataNascimento);
  // Registros antigos guardam turmas de uma divisão anterior (ex.: "4/5 anos"); só confiamos na
  // turma gravada quando ela já está nas faixas atuais, senão recalculamos pela idade.
  const turmaGravada = normalizeString(raw.turma);
  const turmaValida = TURMAS.includes(turmaGravada) ? turmaGravada : '';
  const turma = turmaValida || (idade > 0 ? turmaPorIdade(idade) : 'Sem turma');

  return {
    ...(raw as Partial<Colonia>),
    id: normalizeString(raw.id, String(index + 1)),
    timestamp: normalizeString(raw.timestamp),
    nomeCompleto,
    dataNascimento,
    idade,
    turma,
    fotoUrl: normalizeString(raw.fotoUrl),
    fotoCrop: raw.fotoCrop as Colonia['fotoCrop'],
    cep: normalizeString(raw.cep),
    endereco: normalizeString(raw.endereco),
    numero: normalizeString(raw.numero),
    bairro: normalizeString(raw.bairro),
    cidade: normalizeString(raw.cidade),
    uf: normalizeString(raw.uf),
    associadoUba: normalizeBoolean(raw.associadoUba),
    codigoAssociado: normalizeString(raw.codigoAssociado),
    gratuito: normalizeBoolean(raw.gratuito),
    valorInscricao: normalizeNumber(raw.valorInscricao, valorTotal),
    tamanhoCamisa: normalizeString(raw.tamanhoCamisa ?? raw.camisa, '-'),
    nomeMae,
    telefoneMae,
    nomePai: normalizeString(raw.nomePai),
    telefonePai: normalizeString(raw.telefonePai),
    nomeRetirada: normalizeString(raw.nomeRetirada, nomeMae),
    parentescoRetirada: normalizeString(raw.parentescoRetirada, 'Responsavel'),
    fotoUrlRetirada: normalizeString(raw.fotoUrlRetirada),
    fotoCropRetirada: raw.fotoCropRetirada as Colonia['fotoCropRetirada'],
    sabeNadar: normalizeBoolean(raw.sabeNadar),
    autorizacaoPiscina: normalizeString(raw.autorizacaoPiscina, 'autorizado') as Colonia['autorizacaoPiscina'],
    possuiAlergia: normalizeBoolean(raw.possuiAlergia),
    alergias: normalizeString(raw.alergias),
    possuiDoenca: normalizeBoolean(raw.possuiDoenca),
    doencas: normalizeString(raw.doencas),
    tomaMedicamento: normalizeBoolean(raw.tomaMedicamento),
    medicamentos: normalizeString(raw.medicamentos),
    medicacaoHiperatividade: normalizeBoolean(raw.medicacaoHiperatividade),
    medicamentosHiperatividade: normalizeString(raw.medicamentosHiperatividade),
    transporteVan: normalizeBoolean(raw.transporteVan),
    participacaoBingo: normalizeBoolean(raw.participacaoBingo),
    valorAdicionais: normalizeNumber(raw.valorAdicionais),
    consentimentoLido: normalizeBoolean(raw.consentimentoLido, true),
    status: normalizeString(raw.status, 'confirmado') as Colonia['status'],
    pago: normalizeBoolean(raw.pago, normalizeString(raw.paymentStatus).toUpperCase() === 'RECEIVED'),
    createdAt,
    updatedAt: parseInscricaoDate(raw.updatedAt as DateLike) || undefined,
    pdfUrl: normalizeString(raw.pdfUrl),
    invoiceUrl: normalizeString(raw.invoiceUrl),
    asaasPaymentId: normalizeString(raw.asaasPaymentId),
    installmentLink: normalizeString(raw.installmentLink),
    installmentCount: normalizeNumber(raw.installmentCount),
    installmentValue: normalizeNumber(raw.installmentValue),
    valorTotal
  };
};

export const fetchAdminInscricoesData = async () => {
  const response = await fetch(`${adminApiBaseUrl.replace(/\/$/, '')}/admin/inscricoes`);
  if (!response.ok) {
    throw new Error('Nao foi possivel carregar inscricoes');
  }

  const payload = await response.json();
  const rawInscricoes: unknown[] = Array.isArray(payload.inscricoes) ? payload.inscricoes : [];

  return rawInscricoes
    .map((inscricao, index) =>
      inscricao && typeof inscricao === 'object'
        ? normalizeInscricao(inscricao as Record<string, unknown>, index)
        : null
    )
    .filter((inscricao): inscricao is Colonia => Boolean(inscricao));
};

export interface FaturaInscricao {
  id: string;
  invoiceUrl: string;
  status: string;
  value: number;
}

export const obterFaturaInscricao = async (id: string): Promise<FaturaInscricao> => {
  const response = await fetch(`${adminApiBaseUrl.replace(/\/$/, '')}/admin/inscricoes/${encodeURIComponent(id)}/fatura`);
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || 'Nao foi possivel localizar a fatura desta inscricao.');
  }
  return payload.fatura as FaturaInscricao;
};

export const regenerarFaturaInscricao = async (id: string): Promise<{ fatura: FaturaInscricao; aviso: string | null }> => {
  const response = await fetch(`${adminApiBaseUrl.replace(/\/$/, '')}/admin/inscricoes/${encodeURIComponent(id)}/regenerar-fatura`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' }
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || 'Nao foi possivel regenerar a fatura.');
  }
  return { fatura: payload.fatura as FaturaInscricao, aviso: payload.aviso || null };
};

export class FaturaSemValorError extends Error {}

export const criarFaturaInscricao = async (
  id: string,
  cpf: string,
  options?: { email?: string; associado?: boolean }
): Promise<FaturaInscricao> => {
  const response = await fetch(`${adminApiBaseUrl.replace(/\/$/, '')}/admin/inscricoes/${encodeURIComponent(id)}/criar-fatura`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ cpf, email: options?.email, associado: options?.associado })
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.success) {
    const mensagem = payload?.error || 'Nao foi possivel criar a fatura.';
    if (payload?.semValor) throw new FaturaSemValorError(mensagem);
    throw new Error(mensagem);
  }
  return payload.fatura as FaturaInscricao;
};

export const updateAdminInscricao = async (id: string, patch: Partial<Colonia>) => {
  const response = await fetch(`${adminApiBaseUrl.replace(/\/$/, '')}/admin/inscricoes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch)
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || 'Não foi possível salvar as alterações.');
  }

  const updated =
    payload.inscricao && typeof payload.inscricao === 'object'
      ? normalizeInscricao(payload.inscricao as Record<string, unknown>, 0)
      : null;
  if (!updated) {
    throw new Error('Resposta inválida ao salvar a inscrição.');
  }
  return updated;
};

export const deleteAdminInscricoes = async (ids: string[]) => {
  const response = await fetch(`${adminApiBaseUrl.replace(/\/$/, '')}/admin/inscricoes`, {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids })
  });
  const payload = await response.json();
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || 'Nao foi possivel excluir as inscricoes.');
  }
  return Number(payload.deleted || ids.length);
};

export interface VerifyPaymentsResult {
  checked: number;
  updated: number;
  skipped: number;
  results: Array<{
    id: string;
    skipped?: boolean;
    reason?: string;
    paymentStatus?: string;
    status?: Colonia['status'];
    pago?: boolean;
    valorTotal?: number;
    invoiceUrl?: string;
    billingType?: string;
    installmentCount?: number;
    installmentValue?: number;
  }>;
}

export const verifyAdminPayments = async (ids: string[]) => {
  const response = await fetch(`${adminApiBaseUrl.replace(/\/$/, '')}/admin/verify-payments`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids })
  });
  const payload = await response.json();
  if (!response.ok || !payload.success) {
    throw new Error(payload.error || 'Nao foi possivel verificar os pagamentos.');
  }
  return payload as VerifyPaymentsResult & { success: true };
};

export const useAdminInscricoesData = () => {
  const [inscricoes, setInscricoes] = useState<Colonia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => {
    setReloadToken((current) => current + 1);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);

    fetchAdminInscricoesData()
      .then((data) => {
        if (!active) return;
        setInscricoes(data);
        setError('');
      })
      .catch(() => {
        if (!active) return;
        setInscricoes([]);
        setError('Nao foi possivel carregar as inscricoes.');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  return { inscricoes, loading, error, reload };
};
